import { defineComponent as _defineComponent } from 'vue'
import { ApiRequest, shiftApi, shiftSwapApi } from '@/api';
import ActionDialog from '@/components/dialog/ActionDialog.vue';
import Dialog from '@/components/dialog/Dialog.vue';
import TextArea from '@/components/inputs/TextArea.vue';
import localization from '@/i18n';
import { dateTimeFormats } from '@/lang/dateTimeFormats';
import { ApiVersion } from '@/lib/api/types';
import { useOpen } from '@/lib/composition/dialog';
import { withLoading } from '@/lib/composition/loading';
import { Icon } from '@/lib/enum/Icon';
import { belongsToEmployee } from '@/lib/filters/employeeFilters';
import { Entity } from '@/lib/realtime/RealtimeEntity';
import { realtimeQuery } from '@/lib/realtime/weak/realtimeFunctions';
import { presentShiftTimes } from '@/lib/schedule-v2/scheduleV2Functions';
import { shiftJobRoleIsVisible } from '@/lib/schedule-v2/shiftV2Functions';
import { required } from '@/plugins/vuelidate';
import store from '@/store';
import { isPast, subtractHours } from '@/util/dateArithmetic';
import { getEntityName } from '@/util/entityFunctions';
import ShiftSummary from '@/views/schedule/components/ShiftSummary.vue';
import useVuelidate from '@vuelidate/core';
import { computed, onMounted, reactive, ref, watch } from 'vue';
import {
  Employee,
  ListShiftReplacementsRequest,
  Shift,
  ShiftApi,
  type ListShiftReplacementsResponse,
} from '../../../api/v1';
import Button from '../../components/buttons/Button.vue';
import InputGroup from '../../components/form/InputGroup.vue';
import EmployeeAutoComplete from '../../components/inputs/EmployeeAutoComplete.vue';
import RadioButtons from '../../components/inputs/RadioButtons.vue';
import LoadingOverlay from '../../components/loaders/LoadingOverlay.vue';
import NoData from '../../components/placeholders/NoData.vue';


export default /*#__PURE__*/_defineComponent({
  __name: 'ShiftSwapCreationDialog',
  props: {
    shift: null,
    value: { type: Boolean }
  },
  emits: ["input", "close"],
  setup(__props: any, { emit }: { emit: ({
  (e: 'input', value: boolean): void;
  (e: 'close'): void;
}), expose: any, slots: any, attrs: any }) {

const props = __props as {
  shift: Shift;
  value: boolean;
}





const loading = ref<boolean>(false);
const replacementShifts = ref<Shift[]>([]);
const activeJobRoles = realtimeQuery(Entity.JobRole).whereActive().fetch();
const showSwapCutOffDialog = ref<boolean>(false);

const swapCutOffHoursBeforeShift = computed<number>(
  () => store.state.settings.scheduleSettings.swapCutOffHoursBeforeShift,
);
const timezone = computed<string>(() => store.getters.timezone);

const open = useOpen(() => props.value, emit);

const form = reactive({
  message: '',
  recipientId: null,
  replacementShiftId: null,
});
const validations = {
  message: {},
  recipientId: { required },
  replacementShiftId: {
    required,
  },
};
const v$ = useVuelidate(validations, form, { $autoDirty: true });

const availableEmployeeIds = computed<number[]>(() =>
  replacementShifts.value.map((s) => s.employeeId),
);

/**
 * Used to populate the RadioButtons 'list' prop.
 * We don't care about 'name', since this is not used.
 * We pass the shift object in, as we use it in the slot content.
 */
const replacementShiftOptions = computed<
  { value: number; name: string; shift: Shift }[]
>(() =>
  replacementShifts.value
    .filter(belongsToEmployee(form.recipientId))
    .map((shift: Shift) => ({ value: shift.id, name: '', shift })),
);

const close = () => {
  open.value = false;
  showSwapCutOffDialog.value = false;
};

const presentShiftDateTime = (shift: Shift): string => {
  const date = localization.d(
    shift.startsAt,
    dateTimeFormats.weekdayLongDayMonthLong,
  );
  const time = presentShiftTimes(shift);
  return `${date} ${time}`;
};

const presentJobRole = (shift: Shift): string => {
  return getEntityName(shift.jobRoleId, activeJobRoles.data);
};

const createShiftSwap = withLoading(loading, async () => {
  await shiftSwapApi
    .createShiftSwap({
      createShiftSwapData: {
        shiftId: props.shift.id,
        replacementShiftId: form.replacementShiftId,
        message: form.message,
      },
    })
    .then(() => {
      close();
    });
});

const employeeIsAvailable = (e: Employee): boolean => {
  return replacementShifts.value.some(belongsToEmployee(e.id));
};

// Ensure the selected shift belongs to the selected employee
watch(
  () => form.recipientId,
  () => {
    form.replacementShiftId = null;
  },
);

onMounted(
  withLoading(loading, async () => {
    const isPastSwapCutOff = isPast(
      subtractHours(
        props.shift.startsAt,
        swapCutOffHoursBeforeShift.value,
        timezone.value,
      ),
    );
    if (isPastSwapCutOff) {
      showSwapCutOffDialog.value = true;
    } else {
      const { data } = await new ApiRequest<
        ApiVersion.V1,
        ShiftApi,
        'listShiftReplacements',
        ListShiftReplacementsRequest,
        ListShiftReplacementsResponse
      >(shiftApi, 'listShiftReplacements', {
        id: props.shift.id,
      }).fetchAll();
      replacementShifts.value = data;
    }
  }),
);

return { __sfc: true,props, emit, loading, replacementShifts, activeJobRoles, showSwapCutOffDialog, swapCutOffHoursBeforeShift, timezone, open, form, validations, v$, availableEmployeeIds, replacementShiftOptions, close, presentShiftDateTime, presentJobRole, createShiftSwap, employeeIsAvailable, ActionDialog, Dialog, TextArea, Icon, shiftJobRoleIsVisible, ShiftSummary, Button, InputGroup, EmployeeAutoComplete, RadioButtons, LoadingOverlay, NoData }
}

})